/* utils.js */

"use strict";

const _ = require("lodash");
const papaparse = require("papaparse");

const templateFileName = "device-template.csv";
const csvDefaultOptions = {
    header: true,
    delimiter: ",",
    skipEmptyLines: true,
};

/**
 * @typedef {Object} fieldDefinition
 * @property {string} title - field column title
 * @property {string} path - field path in device
 * @property {string} type - field dataType
 * @property {boolean} [required] - Indicates if field is required
 * @property {object} [typeInfo] - custom field type info
 * @property {boolean} [isCustomField] - indicates if field is custom field
 */

/**
 * @type {fieldDefinition[]}
 */
const baseFieldsData = [
    {
        title: "Name",
        path: "name",
        type: "string",
        required: true,
    },
    {
        title: "Description",
        path: "description",
        type: "string",
        required: false,
    },
    {
        title: "Management State",
        required: false,
        path: "managementState",
        type: "string",
    },
    {
        title: "Discovery Level",
        path: "discoveryLevel",
        type: "string",
    },
    {
        title: "Site Name",
        required: false,
        path: "siteName",
        type: "string",
        excludeFromTemplate: false,
    },
    {
        title: "Environment",
        required: false,
        path: "environment",
        type: "string",
    },
    {
        title: "Connection Method",
        path: "connectionMethod",
        type: "string",
    },
    {
        title: "IP Address",
        path: "connectionInformation.ip",
        type: "string",
    },
    {
        title: "Credential",
        path: "connectionInformation.credentials",
        type: "string",
    },
    {
        title: "Enable Mode Credential",
        path: "connectionInformation.enablePassword",
        type: "string",
    },
    {
        title: "Protocol",
        path: "connectionInformation.type",
        type: "string",
    },
    {
        title: "Port",
        path: "connectionInformation.port",
        type: "number",
    },
    {
        title: "Proxy IP Address",
        path: "connectionInformation.proxyList[0].ip",
        type: "string",
    },
    {
        title: "Proxy Credential",
        path: "connectionInformation.proxyList[0].credentials",
        type: "string",
    },
    {
        title: "Proxy Protocol",
        path: "connectionInformation.proxyList[0].type",
        type: "string",
    },
    {
        title: "Proxy Port",
        path: "connectionInformation.proxyList[0].port",
        type: "number",
    },
    /*
    {
        title: "File Server",
        path: "fileServerName",
        type: "string",
        OSMSpecific: true
    },
    {
        title: "VRF",
        path: "vrf",
        type: "string",
        OSMSpecific: true
    },
    {
        title: "Non-disruptive",
        path: "useIssu",
        type: "boolean",
        OSMSpecific: true
    },

     */
    {
        title: "Zone",
        path: "zoneName",
        type: "string",
    },
    /*
    {
        title: "Controller",
        path: "isController",
        type: "boolean"
    },

     */
    /*,
        {
            title: "HA Group",
            path: "haPairName",
            type: "string",
            OSMSpecific: true
        }*/
    {
        title: "Restore Commands",
        path: "restoreCommands",
        type: "string",
    },
    {
        title: "Add Tags",
        path: "addTagIds",
        type: "tags",
    },
    {
        title: "Remove Tags",
        path: "removeTagIds",
        type: "tags",
    },
    {
        title: "Replace Tags",
        path: "tagIds",
        type: "tags",
    },
];

/**
 * format custom fields data
 * @param {Array<{label:string, value:string, [systemOwned]:boolean, [enabled]:boolean, [typeInfo]:Object}>} fields
 * @return {fieldDefinition[]}
 */
const formatCustomFields = (fields) =>
    fields.map((field) => ({
        title: field.label,
        path: `customFieldValues.${field.value}`,
        type: field.type,
        typeInfo: field.typeInfo,
        isCustomField: true,
    }));

const requiredFieldName = (title) => `** ${title} **`;

const getFieldsLabels = (fieldsData) =>
    fieldsData
        .filter((col) => {
            return !col.excludeFromTemplate;
        })
        .map(({title, required}) => (required ? requiredFieldName(title) : title));

const generateCSV = (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const fields = _.get(csvOptions, "columns", []);

    return papaparse.unparse({fields, data});
};

const parseCSV = async (data, options) => {
    const csvOptions = _.defaults(options, csvDefaultOptions);
    const resp = await papaparse.parse(data, csvOptions, (err, data) =>
        err ? Promise.reject(err) : Promise.resolve(data)
    );
    if (resp.data && resp.data[0]) {
        let respArray = [Object.keys(resp.data[0])];
        resp.data.forEach(function (item) {
            respArray.push(Object.values(item));
        });
        return Promise.resolve(respArray);
    }
    return Promise.reject(resp.errors[0] || "unknown");
};

/**
 * match headers with fields definitions
 * @param {string[]} headers
 * @param {fieldDefinition[]} fieldsDefinitions
 * @return {fieldDefinition[]}
 */
const getListOfHeadersDefinitions = (fieldsDefinitions, headers) =>
    headers.map((headerName) => {
        headerName = headerName.replace(/^\*+/, "").replace(/\*+$/, "").trim();
        return fieldsDefinitions.find(({title}) => _.lowerCase(title) === _.lowerCase(headerName));
    });

module.exports = {
    templateFileName,
    baseFieldsData,
    getFieldsLabels,
    formatCustomFields,
    generateCSV,
    parseCSV,
    getListOfHeadersDefinitions,
};
